// contexts/GraphProvider.tsx
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { GraphContext, type RefreshDataParams } from './GraphContext';
import {
    getBookingPerformanceCost,
    getCampaignUplift,
    getChannelPerformanceCost,
    getCreativePerformanceCost,
    getImpressionHeatmap,
    getImpressionsBeforeVisit,
    getImpressionSummary,
    getReachBreakdown,
    getResponsesByRegion,
    getResponsesBySite,
    getResponsesOverTime,
    getUniqueReach,
    getVisitResponseTiming,
    streamAnalysis,
} from '@/api';
import { formatDateForApi } from '@/utils';

export const GraphProvider = ({ children }: { children: ReactNode }) => {
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedClient, setSelectedClient] = useState('');
    const [impressionSummary, setImpressionSummary] = useState<unknown>(null);
    const [impressionsBeforeVisit, setImpressionsBeforeVisit] = useState<unknown>(null);
    const [responsesByRegion, setResponsesByRegion] = useState<unknown>(null);
    const [responsesBySite, setResponsesBySite] = useState<unknown>(null);
    const [responsesOverTime, setResponsesOverTime] = useState<unknown>(null);
    const [visitResponseTiming, setVisitResponseTiming] = useState<unknown>(null);
    const [creativePerformanceCost, setCreativePerformanceCost] = useState<unknown>(null);
    const [channelPerformanceCost, setChannelPerformanceCost] = useState<unknown>(null);
    const [bookingPerformanceCost, setBookingPerformanceCost] = useState<unknown>(null);
    const [impressionHeatmap, setImpressionHeatmap] = useState<unknown>(null);
    const [campaignUplift, setCampaignUplift] = useState<unknown>(null);
    const [uniqueReach, setUniqueReach] = useState<unknown>(null);
    const [reachBreakdown, setReachBreakdown] = useState<unknown>(null);
    const [hasGraphData, setHasGraphData] = useState(false);
    const [dataEndDate, setDataEndDate] = useState<string | null>(null);
    const [analysisResult, setAnalysisResult] = useState<unknown>(null);
    const [analysisStreamText, setAnalysisStreamText] = useState('');
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [optimisationResult, setOptimisationResult] = useState<unknown>(null);
    const [optimisationStreamText, setOptimisationStreamText] = useState('');
    const [optimisationLoading, setOptimisationLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const analysisAbortRef = useRef<AbortController | null>(null);
    const optimisationAbortRef = useRef<AbortController | null>(null);

    const requestTrackingRef = useRef<Set<string>>(new Set());
    const abortControllerRef = useRef<AbortController | null>(null);
    const analysisCacheRef = useRef<Map<string, unknown>>(new Map());
    const optimisationCacheRef = useRef<Map<string, unknown>>(new Map());

    const resetData = useCallback(() => {
        setImpressionSummary(null);
        setImpressionsBeforeVisit(null);
        setResponsesByRegion(null);
        setResponsesBySite(null);
        setResponsesOverTime(null);
        setVisitResponseTiming(null);
        setCreativePerformanceCost(null);
        setChannelPerformanceCost(null);
        setBookingPerformanceCost(null);
        setImpressionHeatmap(null);
        setCampaignUplift(null);
        setUniqueReach(null);
        setReachBreakdown(null);
        setAnalysisResult(null);
        setAnalysisStreamText('');
        setOptimisationResult(null);
        setOptimisationStreamText('');
        setHasGraphData(false);
        setDataEndDate(null);
    }, []);

    const refreshData = useCallback(
        async ({
            clientId,
            selectedDateRange,
            selectedRegions,
            selectedCampaigns,
            selectedBookings,
            selectedCreatives,
            selectedChannels,
            selectedWeekdays,
            selectedDayparts,
            selectedSites,
        }: RefreshDataParams) => {
            let startdate: string | null = null;
            let enddate: string | null = null;

            try {
                if (selectedDateRange?.startDate) startdate = formatDateForApi(selectedDateRange.startDate);
            } catch (e) {
                console.warn('GraphProvider: failed to format startDate', selectedDateRange?.startDate, e);
            }
            try {
                if (selectedDateRange?.endDate) enddate = formatDateForApi(selectedDateRange.endDate);
            } catch (e) {
                console.warn('GraphProvider: failed to format endDate', selectedDateRange?.endDate, e);
            }

            const data = {
                client_id: clientId,
                startdate,
                enddate,
                region: selectedRegions ?? [0],
                campaign: selectedCampaigns ?? [0],
                booking: selectedBookings ?? [0],
                creative: selectedCreatives ?? [0],
                channel: selectedChannels ?? [0],
                weekday: selectedWeekdays ?? [0],
                time_slots: selectedDayparts ?? [0],
                sites: selectedSites ?? [0],
            };

            const requestSignature = JSON.stringify(data);

            if (requestTrackingRef.current.has(requestSignature)) {
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            requestTrackingRef.current.add(requestSignature);
            setLoading(true);

            try {
                // Fetch all widget data in parallel
                const [
                    fetchedImpressionSummary,
                    fetchedImpressionsBeforeVisit,
                    fetchedResponsesByRegion,
                    fetchedResponsesBySite,
                    fetchedResponsesOverTime,
                    fetchedVisitResponseTiming,
                    fetchedCreativePerformanceCost,
                    fetchedChannelPerformanceCost,
                    fetchedBookingPerformanceCost,
                    fetchedImpressionHeatmap,
                    fetchedCampaignUplift,
                    fetchedUniqueReach,
                    fetchedReachBreakdown,
                ] = await Promise.all([
                    getImpressionSummary(data, signal),
                    getImpressionsBeforeVisit(data, signal),
                    getResponsesByRegion(data, signal),
                    getResponsesBySite(data, signal),
                    getResponsesOverTime(data, signal),
                    getVisitResponseTiming(data, signal),
                    getCreativePerformanceCost(data, signal),
                    getChannelPerformanceCost(data, signal),
                    // Newer than the backend it may meet: a failure hides the Booking cut, never the dashboard
                    getBookingPerformanceCost(data, signal).catch(() => null),
                    getImpressionHeatmap(data, signal),
                    getCampaignUplift(data, signal),
                    getUniqueReach(data, signal),
                    getReachBreakdown(data, signal),
                ]);
                // The booking fetch never rejects, so an abort can arrive as a result: drop it
                if (signal.aborted) return;

                resetData();

                if ((fetchedImpressionSummary as Record<string, unknown>)?.summary_data) {
                    setImpressionSummary(fetchedImpressionSummary);
                    setImpressionsBeforeVisit(fetchedImpressionsBeforeVisit);
                    setResponsesByRegion(fetchedResponsesByRegion);
                    setResponsesBySite(fetchedResponsesBySite);
                    setResponsesOverTime(fetchedResponsesOverTime);
                    setVisitResponseTiming(fetchedVisitResponseTiming);
                    setCreativePerformanceCost(fetchedCreativePerformanceCost);
                    setChannelPerformanceCost(fetchedChannelPerformanceCost);
                    setBookingPerformanceCost(fetchedBookingPerformanceCost);
                    setImpressionHeatmap(fetchedImpressionHeatmap);
                    setCampaignUplift(fetchedCampaignUplift);
                    setUniqueReach(fetchedUniqueReach);
                    setReachBreakdown(fetchedReachBreakdown);
                    setDataEndDate(enddate);
                    setHasGraphData(true);
                    setAnalysisResult(null);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error('GraphProvider: error fetching data:', error);
                }
            } finally {
                requestTrackingRef.current.delete(requestSignature);
                setLoading(false);
            }
        },
        [resetData]
    );

    const buildAnalysisPayload = useCallback((params: RefreshDataParams, tone: string) => {
        let startdate: string | null = null;
        let enddate: string | null = null;
        try {
            if (params.selectedDateRange?.startDate) startdate = formatDateForApi(params.selectedDateRange.startDate);
        } catch {
            /* ignore */
        }
        try {
            if (params.selectedDateRange?.endDate) enddate = formatDateForApi(params.selectedDateRange.endDate);
        } catch {
            /* ignore */
        }

        return {
            client_id: params.clientId,
            startdate,
            enddate,
            region: params.selectedRegions ?? [0],
            campaign: params.selectedCampaigns ?? [0],
            booking: params.selectedBookings ?? [0],
            creative: params.selectedCreatives ?? [0],
            channel: params.selectedChannels ?? [0],
            weekday: params.selectedWeekdays ?? [0],
            time_slots: params.selectedDayparts ?? [0],
            sites: params.selectedSites ?? [0],
            tone,
            format: 'html',
        };
    }, []);

    const fetchAnalysis = useCallback(
        (params: RefreshDataParams) => {
            if (!impressionSummary) return;

            const payload = buildAnalysisPayload(params, 'positive');
            const cacheKey = JSON.stringify(payload);
            const cached = analysisCacheRef.current.get(cacheKey);

            if (cached) {
                setAnalysisResult(cached);
                setAnalysisStreamText('');
                return;
            }

            analysisAbortRef.current?.abort();
            const controller = new AbortController();
            analysisAbortRef.current = controller;

            setAnalysisLoading(true);
            setAnalysisResult(null);
            setAnalysisStreamText('');

            streamAnalysis(
                payload,
                (text) => setAnalysisStreamText((prev) => prev + text),
                (result) => {
                    analysisCacheRef.current.set(cacheKey, result);
                    setAnalysisResult(result);
                    setAnalysisLoading(false);
                },
                (error) => {
                    console.error('Analysis error:', error);
                    setAnalysisLoading(false);
                },
                controller.signal
            ).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Analysis stream error:', error);
                    setAnalysisLoading(false);
                }
            });
        },
        [impressionSummary, buildAnalysisPayload]
    );

    const fetchOptimisation = useCallback(
        (params: RefreshDataParams) => {
            if (!impressionSummary) return;

            const payload = buildAnalysisPayload(params, 'candid');
            const cacheKey = JSON.stringify(payload);
            const cached = optimisationCacheRef.current.get(cacheKey);

            if (cached) {
                setOptimisationResult(cached);
                setOptimisationStreamText('');
                return;
            }

            optimisationAbortRef.current?.abort();
            const controller = new AbortController();
            optimisationAbortRef.current = controller;

            setOptimisationLoading(true);
            setOptimisationResult(null);
            setOptimisationStreamText('');

            streamAnalysis(
                payload,
                (text) => setOptimisationStreamText((prev) => prev + text),
                (result) => {
                    optimisationCacheRef.current.set(cacheKey, result);
                    setOptimisationResult(result);
                    setOptimisationLoading(false);
                },
                (error) => {
                    console.error('Optimisation error:', error);
                    setOptimisationLoading(false);
                },
                controller.signal
            ).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Optimisation stream error:', error);
                    setOptimisationLoading(false);
                }
            });
        },
        [impressionSummary, buildAnalysisPayload]
    );

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
            analysisAbortRef.current?.abort();
            optimisationAbortRef.current?.abort();
        };
    }, []);

    return (
        <GraphContext.Provider
            value={{
                impressionSummary,
                impressionsBeforeVisit,
                responsesByRegion,
                responsesBySite,
                responsesOverTime,
                visitResponseTiming,
                creativePerformanceCost,
                channelPerformanceCost,
                bookingPerformanceCost,
                impressionHeatmap,
                campaignUplift,
                uniqueReach,
                reachBreakdown,
                refreshData,
                resetData,
                hasGraphData,
                dataEndDate,
                analysisResult,
                analysisStreamText,
                analysisLoading,
                fetchAnalysis,
                optimisationResult,
                optimisationStreamText,
                optimisationLoading,
                fetchOptimisation,
                loading,
                selectedCustomer,
                setSelectedCustomer,
                selectedClient,
                setSelectedClient,
            }}
        >
            {children}
        </GraphContext.Provider>
    );
};
